import { ExternalLink, Star } from "lucide-react";

export const fmtRuntime = (m) => (m ? (m >= 60 ? `${Math.floor(m / 60)} h ${m % 60} min` : `${m} min`) : null);
const fmtVotes = (n) => (n >= 1000 ? `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k` : `${n}`);
const fmtDate = (d) => (d ? new Date(d + "T00:00:00").toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : null);

export const Rating = ({ value, votes, className = "", testid }) => (value ? (
  <span className={`inline-flex items-center gap-1 ${className}`} title={votes ? `${value} / 10 · ${votes.toLocaleString()} votes on TMDB` : `${value} / 10 on TMDB`} data-testid={testid}>
    <Star size={11} className="fill-amber-400 text-amber-400" />{value}{votes ? <span className="text-zinc-400"> · {fmtVotes(votes)}</span> : null}
  </span>
) : null);

/* Plot / rating / runtime block from TMDB for a movie or a show (d = tmdb data). */
export const TmdbDetails = ({ d, compact = false, testid = "tmdb-details" }) => {
  if (!d) return null;
  const facts = [
    d.certification ? <span key="c" className="rounded border border-zinc-300 px-1.5 py-px text-[10px] font-semibold uppercase tracking-wide">{d.certification}</span> : null,
    d.year ? <span key="y">{d.year}{d.year_end && d.year_end !== d.year ? `–${d.year_end}` : d.status && d.status !== "Ended" && d.seasons ? "–" : ""}</span> : null,
    d.runtime ? <span key="r">{fmtRuntime(d.runtime)}{d.seasons ? " episodes" : ""}</span> : null,
    d.seasons ? <span key="s">{d.seasons} season{d.seasons === 1 ? "" : "s"}{d.episodes ? ` · ${d.episodes} episodes` : ""}</span> : null,
  ].filter(Boolean);
  return (
    <div className={`space-y-2 ${compact ? "max-w-2xl" : "max-w-3xl"}`} data-testid={testid}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-zinc-600">
        <Rating value={d.rating} votes={d.votes} testid={`${testid}-rating`} />
        {facts}
        {d.genres?.length ? <span className="text-zinc-500" data-testid={`${testid}-genres`}>{d.genres.join(" · ")}</span> : null}
      </div>
      {d.tagline && <p className="text-sm italic text-zinc-500" data-testid={`${testid}-tagline`}>{d.tagline}</p>}
      {d.overview && <p className={`text-zinc-700 ${compact ? "text-sm leading-relaxed" : "text-sm leading-relaxed sm:text-[15px]"}`} data-testid={`${testid}-overview`}>{d.overview}</p>}
      {d.url && <a href={d.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[11px] text-zinc-400 hover:text-zinc-900" data-testid={`${testid}-link`}>The Movie Database<ExternalLink size={10} /></a>}
    </div>
  );
};

/* Episode plot + air date + rating (e = episode_info). */
export const EpisodeDetails = ({ e, testid = "episode-details" }) => {
  if (!e || !(e.overview || e.air_date || e.rating)) return null;
  return (
    <div className="max-w-3xl space-y-1.5" data-testid={testid}>
      <div className="flex flex-wrap items-center gap-x-3 text-xs text-zinc-600">
        <Rating value={e.rating} testid={`${testid}-rating`} />
        {e.air_date && <span>Aired {fmtDate(e.air_date)}</span>}
        {e.runtime ? <span>{fmtRuntime(e.runtime)}</span> : null}
      </div>
      {e.overview && <p className="text-sm leading-relaxed text-zinc-700" data-testid={`${testid}-overview`}>{e.overview}</p>}
    </div>
  );
};
